
import { Vue, Options } from "vue-class-component";
import {
  ellipsisString,
  parsingErrorResponse
} from "@/app/infrastructures/misc/Utils";
import router from "@/app/ui/router";
import { CargoV2Controller } from "@/app/ui/controllers/CargoV2Controller";
import { ReportController } from "@/app/ui/controllers/ReportController";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { FlagsPermissionCargo } from "@/feature-flags/flags-permission-cargo";
import { CargoV2Data } from "@/domain/entities/CargoV2";
import { CargoV2ListRequest } from "@/data/payload/api/CargoV2ApiRequest";
import { Pagination } from "@/domain/entities/Pagination";
import CargoV2FilterSection from "./components/cargo-v2-filter-section.vue";
import WidgetDownloadCargo from "@/app/ui/views/cargo/component/widget-download-cargo.vue";
import { AccountController } from "../../controllers/AccountController";

@Options({
  components: { CargoV2FilterSection, WidgetDownloadCargo }
})
export default class CargoV2BookingCancelled extends Vue {
  showModalDelete = false;
  showModalReBooking = false;
  selectedItem: CargoV2Data | null = null;

  localFilter = new CargoV2ListRequest();

  get cancelledList(): CargoV2Data[] {
    return CargoV2Controller.cancelledList.data;
  }

  get cancelledPagination(): Pagination {
    return CargoV2Controller.cancelledPagination;
  }

  set cancelledPagination(p: Pagination) {
    CargoV2Controller.setCancelledPagination(p);
  }

  get isLoading(): boolean {
    return CargoV2Controller.isLoading;
  }

  get errorCause(): string {
    return CargoV2Controller.errorCause;
  }

  get cargoListAvailable(): boolean {
    return (
      !this.cancelledList.length &&
      !this.isLoading &&
      (this.cancelledPagination?.page ?? 1) === 1
    );
  }

  get deleteModalMessage(): string {
    const no = this.selectedItem
      ? this.displayBookingNo(this.selectedItem)
      : "";
    return this.$t("cargo.messages.deleteBookingMessage", {
      bookingNumber: no
    });
  }

  get rebookModalMessage(): string {
    const no = this.selectedItem
      ? this.displayBookingNo(this.selectedItem)
      : "";
    return this.$t("cargo.messages.rebookBookingMessage", {
      bookingNumber: no
    });
  }

  get columns() {
    return [
      {
        name: this.$t("cargo.labels.bookingNumber"),
        styleHead: "w-56 text-left whitespace-no-wrap align-top break-all",
        "custom-slot": true,
        slotName: "no_booking"
      },
      {
        name: this.$t("cargo.labels.originDestinationCities"),
        styleHead:
          "min-w-[12rem] w-40 text-left whitespace-nowrap align-middle",
        "custom-slot": true,
        slotName: "origin_destination"
      },
      {
        name: this.$t("cargo.labels.cargoStatus"),
        styleHead: "w-26 text-left whitespace-no-wrap",
        render: (item: CargoV2Data) =>
          `<div class="overflow-ellipsis text-left">${this.cargoStatusLabel(
            item.cargoStatus
          )}</div>`
      },
      {
        name: this.$t("cargo.v2.column.reason"),
        styleHead: "w-44 text-left whitespace-no-wrap",
        render: (item: CargoV2Data) => {
          const fallbackText = this.$t(
            "cargo.v2.empty.reasonUnavailable"
          ) as string;
          return `<div class="overflow-ellipsis text-left text-red-lp-100">${ellipsisString(
            item.reasonFailed || fallbackText,
            80
          )}</div>`;
        }
      },
      {
        name: this.$t("cargo.labels.lastUpdated"),
        styleHead: "w-56 text-left whitespace-no-wrap align-middle",
        render: (item: CargoV2Data) => {
          const date = item.updatedAt
            ? this.formatDate(item.updatedAt, "DD MMMM YYYY, HH:mm")
            : "-";
          const translateText = this.$t("cargo.v2.datetime.timezoneSuffix", {
            date
          });
          return item.updatedAt
            ? `<div class="flex flex-col text-sm text-gray-lp-300">
                  <div class="overflow-ellipsis text-left"> ${translateText} </div>
                  <div class="truncate text-gray-lp-600 w-32">
                    ${item.updatedBy || "-"}
                  </div>
                  <div> ${item.cargoHubName || "-"} </div>
                </div>`
            : `<div class="overflow-ellipsis text-left"> - </div>`;
        }
      },
      {
        name: this.$t("cargo.labels.manage"),
        styleHead: "w-40 text-left",
        "custom-slot": true,
        slotName: "atur_cancel_booking"
      }
    ];
  }

  displayBookingNo(item: CargoV2Data): string {
    const no = item.cargoNumber || item.bookingRequestId || "";
    return ellipsisString(no, 40) || "-";
  }

  cargoStatusLabel(status: string): string {
    return status === "failed"
      ? this.$t("cargo.messages.failedStatus")
      : this.$t("cargo.messages.canceledStatus");
  }

  formatDate(dateStr: string, format = "DD MMMM YYYY"): string {
    if (!dateStr) return "";
    return this.$moment(dateStr).format(format);
  }

  openModalDelete(item: CargoV2Data): void {
    this.selectedItem = item;
    CargoV2Controller.setCargoIdToDelete(item.id);
    CargoV2Controller.setCargoNoForModal(item.cargoNumber);
    this.showModalDelete = true;
  }

  closeModalDelete(): void {
    this.showModalDelete = false;
    this.selectedItem = null;
    CargoV2Controller.setCargoIdToDelete(null);
  }

  async confirmDelete(): Promise<void> {
    await CargoV2Controller.deleteBooking();
    this.closeModalDelete();
    await this.fetchList();
  }

  openModalReBooking(item: CargoV2Data): void {
    this.selectedItem = item;
    this.showModalReBooking = true;
  }

  closeModalReBooking(): void {
    this.showModalReBooking = false;
    this.selectedItem = null;
  }

  goToReBooking(): void {
    if (!this.selectedItem) return;
    router.push(
      `/cargo-v2/${encodeURIComponent(this.selectedItem.id)}/rebooking`
    );
    this.closeModalReBooking();
  }

  onSearch(value: string) {
    this.localFilter.search = value;

    const p = CargoV2Controller.pagination;
    CargoV2Controller.setPagination(new Pagination(1, p?.limit, p?.totalData));

    this.fetchList();
  }
  onFilterChange(payload: CargoV2ListRequest): void {
    this.localFilter = payload;
    const p = CargoV2Controller.cancelledPagination;
    CargoV2Controller.setCancelledPagination(
      new Pagination(1, p?.limit ?? 20, p?.totalData ?? 0)
    );
    this.fetchList();
  }

  async fetchList(): Promise<void> {
    await CargoV2Controller.fetchListCancelled(
      new CargoV2ListRequest({
        ...this.localFilter,
        originCity: this.originCity,
        page: CargoV2Controller.cancelledPagination?.page,
        limit: CargoV2Controller.cancelledPagination?.limit
      })
    );
  }

  get originCity() {
    return (
      this.localFilter.originCity ||
      AccountController.accountData.account_location.city_code
    );
  }

  onClickRow(item: CargoV2Data): void {
    router.push(
      `/cargo-v2/${encodeURIComponent(item.cargoNumber)}/info?rc_no=${item.id}`
    );
  }

  downloadFailedParams(): CargoV2ListRequest {
    return new CargoV2ListRequest({
      ...this.localFilter,
      status: "cancel,failed"
    });
  }

  async onDownloadFailedList(): Promise<void> {
    MainAppController.showLoading();
    try {
      await CargoV2Controller.downloadListCargoFailed(
        this.downloadFailedParams()
      );
      ReportController.setOpenDownloadRequest(true);
      ReportController.listDownloadReport.pagination.page = 1;
      ReportController.listDownloadReport.pagination.limit = 20;
      ReportController.onGetListDownloadReport("retry-cargo-failed-report");
    } catch (error) {
      console.log(error);
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          this.$t("cargo.v2.errors.downloadFailedList"),
          () => {
            MainAppController.closeErrorMessage();
          }
        )
      );
    } finally {
      MainAppController.closeLoading();
    }
  }

  fetchInterval: ReturnType<typeof setInterval> | null = null;

  startInterval() {
    this.fetchInterval = setInterval(() => {
      this.fetchLastIdRetryCargo();
    }, FlagsPermissionCargo.config_interval_auto_refresh_cargo.getValue() * 1000);
  }

  stopInterval(): void {
    if (this.fetchInterval) {
      clearInterval(this.fetchInterval);
      this.fetchInterval = null;
    }
  }

  lastIdRetryCargo = 0;
  async fetchLastIdRetryCargo() {
    const newLastIdRetryCargo = await CargoV2Controller.fetchLastIdCargoRetryCargo(
      new CargoV2ListRequest({ type: "retry-cargo" })
    );
    if (this.lastIdRetryCargo === 0 && newLastIdRetryCargo !== 0) {
      this.lastIdRetryCargo = newLastIdRetryCargo;
    }
    if (this.lastIdRetryCargo === 0 || newLastIdRetryCargo === 0) return;
    if (this.lastIdRetryCargo !== newLastIdRetryCargo) {
      this.lastIdRetryCargo = newLastIdRetryCargo;
      // await this.fetchRetryCargoListWithoutLoading();
    }
  }

  mounted(): void {
    this.fetchLastIdCargo();
    this.startInterval();
  }

  unmounted(): void {
    this.stopInterval();
  }

  lastIdCargo = 0;
  async fetchLastIdCargo() {
    const newLastIdCargo = await CargoV2Controller.fetchLastIdCargoRetryCargo(
      new CargoV2ListRequest({ type: "retry-cargo" })
    );
    if (this.lastIdCargo === 0 && newLastIdCargo !== 0) {
      this.lastIdCargo = newLastIdCargo;
    }
    if (this.lastIdCargo === 0 || newLastIdCargo === 0) return;
    if (this.lastIdCargo !== newLastIdCargo) {
      this.lastIdCargo = newLastIdCargo;
      await this.fetchList();
    }
  }
}
